import { getCurrentInstance, computed, ref } from '@vue/composition-api'
import {
    dummyBoardDocuments,
    dates,
    estimatedCost,
    monthCost,
    months,
    prevmonth,
    products,
    regions,
    statistics,
    weeks
} from '@/modules/Dashboard/_store/dummy-data'

export default function useDashboard (STORE_KEY = '$_dashboard') {
    const vm = getCurrentInstance().proxy
    const store = vm.$store

    const dashboardEditMode = computed(
        () => store.getters['appConfig/dashboardEditMode']
    )

    const boardDocuments = computed(() => store.getters.boardDocuments)
    const boardDocumentsStatus = computed(
        () => store.getters.boardDocumentsStatus
    )
    const getBoardDocuments = (params) => [
        boardDocumentsStatus,
        dashboardEditMode.value ? ref(dummyBoardDocuments) : boardDocuments,
        params && !dashboardEditMode.value
            ? store.dispatch('getBoardDocuments')
            : null
    ]

    const summaryStatisticsStatus = computed(
        () => store.getters[`${STORE_KEY}/summaryStatisticsStatus`]
    )
    const summaryStatistics = computed(
        () => store.getters[`${STORE_KEY}/summaryStatistics`]
    )
    const getSummaryStatistics = (params) => [
        summaryStatisticsStatus,
        dashboardEditMode.value ? ref(statistics) : summaryStatistics,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getSummaryStatistics`, params)
            : null
    ]

    const summaryMonthsStatus = computed(
        () => store.getters[`${STORE_KEY}/summaryMonthsStatus`]
    )
    const summaryMonths = computed(
        () => store.getters[`${STORE_KEY}/summaryMonths`]
    )
    const getSummaryMonths = (params) => [
        summaryMonthsStatus,
        dashboardEditMode.value ? ref(months) : summaryMonths,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getSummaryMonths`, params)
            : null
    ]

    const summaryRegionsStatus = computed(
        () => store.getters[`${STORE_KEY}/summaryRegionsStatus`]
    )
    const summaryRegions = computed(
        () => store.getters[`${STORE_KEY}/summaryRegions`]
    )
    const getSummaryRegions = (params) => [
        summaryRegionsStatus,
        dashboardEditMode.value ? ref(regions) : summaryRegions,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getSummaryRegions`, params)
            : null
    ]

    const summaryWeeksStatus = computed(
        () => store.getters[`${STORE_KEY}/summaryWeeksStatus`]
    )
    const summaryWeeks = computed(
        () => store.getters[`${STORE_KEY}/summaryWeeks`]
    )
    const getSummaryWeeks = (params) => [
        summaryWeeksStatus,
        dashboardEditMode.value ? ref(weeks) : summaryWeeks,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getSummaryWeeks`, params)
            : null
    ]

    const summaryDatesStatus = computed(
        () => store.getters[`${STORE_KEY}/summaryDatesStatus`]
    )
    const summaryDates = computed(
        () => store.getters[`${STORE_KEY}/summaryDates`]
    )
    const getSummaryDates = (params) => [
        summaryDatesStatus,
        dashboardEditMode.value ? ref(dates) : summaryDates,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getSummaryDates`, params)
            : null
    ]

    const summaryProductsStatus = computed(
        () => store.getters[`${STORE_KEY}/summaryProductsStatus`]
    )
    const summaryProducts = computed(
        () => store.getters[`${STORE_KEY}/summaryProducts`]
    )
    const getSummaryProducts = (params) => [
        summaryProductsStatus,
        dashboardEditMode.value ? ref(products) : summaryProducts,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getSummaryProducts`, params)
            : null
    ]

    const compareEstimatedStatus = computed(
        () => store.getters[`${STORE_KEY}/compareEstimatedStatus`]
    )
    const compareEstimated = computed(
        () => store.getters[`${STORE_KEY}/compareEstimated`]
    )
    const getCompareEstimated = (params) => [
        compareEstimatedStatus,
        dashboardEditMode.value ? ref(estimatedCost) : compareEstimated,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getCompareEstimated`, params)
            : null
    ]

    const comparePrevMonthStatus = computed(
        () => store.getters[`${STORE_KEY}/comparePrevMonthStatus`]
    )
    const comparePrevMonth = computed(
        () => store.getters[`${STORE_KEY}/comparePrevMonth`]
    )
    const getComparePrevMonth = (params) => [
        comparePrevMonthStatus,
        dashboardEditMode.value ? ref(prevmonth) : comparePrevMonth,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getComparePrevMonth`, params)
            : null
    ]

    const estimatable = (month) =>
        month === new Date().toISOString().substring(0, 7)
    const totalCostStatus = computed(
        () => store.getters[`${STORE_KEY}/totalCostStatus`]
    )
    const totalCost = computed(() => store.getters[`${STORE_KEY}/totalCost`])
    const getTotalCost = (params) => [
        totalCostStatus,
        dashboardEditMode.value ? ref(estimatedCost) : totalCost,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getTotalCost`, {
                ...params,
                isEstimated: estimatable(params.month)
            })
            : null
    ]

    const costStatus = computed(() => store.getters[`${STORE_KEY}/costStatus`])
    const cost = computed(() => store.getters[`${STORE_KEY}/cost`])
    const getCostByMonth = (currentParams, localParams = currentParams) => [
        costStatus,
        dashboardEditMode.value ? ref(monthCost) : cost,
        currentParams && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getCostByMonth`, {
                currentParams,
                localParams
            })
            : null
    ]

    const summaryDetailStatus = computed(
        () => store.getters[`${STORE_KEY}/detailStatus`]
    )
    const summaryDetail = computed(() => store.getters[`${STORE_KEY}/detail`])
    const getSummaryDetail = (params) => [
        summaryDetailStatus,
        summaryDetail,
        params && !dashboardEditMode.value
            ? store.dispatch(`${STORE_KEY}/getSummaryDetail`, params)
            : null
    ]

    const loadingStatus = computed(
        () => store.getters[`${STORE_KEY}/loadingStatus`]
    )

    return {
        dashboardEditMode,
        getBoardDocuments,
        getSummaryStatistics,
        getSummaryMonths,
        getSummaryRegions,
        getSummaryWeeks,
        getSummaryDates,
        getSummaryProducts,
        getCompareEstimated,
        getComparePrevMonth,
        getTotalCost,
        getCostByMonth,
        summaryDetailStatus,
        summaryDetail,
        getSummaryDetail,
        loadingStatus
    }
}
